/**
 * RuleExplanation — the ANA.6 static explanation (sheets 04 and 21; mission 6
 * WP03 T023; FR-005, NFR-007; US6.7; post-tasks P13).
 *
 * Reads `['rule', id]` through `ruleQuery`, and renders five blocks in order:
 * what this checks, why it matters, the CRRG reference (a mono citation, never
 * the licensed text), common causes, and the suggested fix split into a data
 * problem and a mapping problem. A draft renders exactly like an approved
 * explanation, with no status shown. A placeholder renders register 11's
 * statement and the CRRG reference. A 404 or a refused id renders the unknown
 * rule line; any other failure, the unavailable line. Never a model call.
 * WP08's drawer and WP10's catalog import it; neither edits it.
 *
 * Mission 8 WP10 (T054; FR-020 A-L5): a block whose text is null is omitted,
 * heading and all, so a draft never shows an empty headed section; a null half
 * of the suggested fix is omitted, and the fix block too when both are null.
 * The CRRG block always renders.
 */
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Skeleton } from '@/components/ui/skeleton'
import { ApiError, InvalidIdError } from '@/lib/api'
import { Mono } from '@/lib/format'
import { ruleQuery } from '@/lib/queries'
import { copyText } from '@/lib/provisional-copy'

export const EXPLANATION_BLOCKS = [
  'What this checks',
  'Why it matters',
  'CRRG reference',
  'Common causes',
  'Suggested fix',
] as const

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h4 className="text-[16px] font-semibold leading-[22px] text-navy-950">{title}</h4>
      {children}
    </section>
  )
}

function Text({ children }: { children: ReactNode }) {
  return <p className="text-body text-navy-950">{children}</p>
}

function notFound(error: unknown): boolean {
  return error instanceof InvalidIdError || (error instanceof ApiError && error.problem.status === 404)
}

export function RuleExplanation({ ruleId }: { ruleId: string }) {
  const query = useQuery(ruleQuery(ruleId))

  if (query.isPending) {
    return (
      <div aria-busy="true" className="flex flex-col gap-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    )
  }
  if (query.isError) {
    return <Text>{copyText(notFound(query.error) ? 'unknownRule' : 'explanationUnavailable')}</Text>
  }

  const rule = query.data
  const e = rule.explanation
  const crrg = (
    <Block title={EXPLANATION_BLOCKS[2]}>
      <Mono className="text-body-sm text-navy-950">{rule.crrg_ref}</Mono>
    </Block>
  )
  if (e.status === 'placeholder') {
    return (
      <div className="flex flex-col gap-6">
        <Text>{copyText('placeholderExplanation')}</Text>
        {crrg}
      </div>
    )
  }
  const fixes: Array<[string, string]> = []
  if (e.suggested_fix.data_problem !== null) fixes.push(['Data problem', e.suggested_fix.data_problem])
  if (e.suggested_fix.mapping_problem !== null) fixes.push(['Mapping problem', e.suggested_fix.mapping_problem])
  return (
    <div className="flex flex-col gap-6">
      {e.what_it_checks !== null ? (
        <Block title={EXPLANATION_BLOCKS[0]}>
          <Text>{e.what_it_checks}</Text>
        </Block>
      ) : null}
      {e.why_it_matters !== null ? (
        <Block title={EXPLANATION_BLOCKS[1]}>
          <Text>{e.why_it_matters}</Text>
        </Block>
      ) : null}
      {crrg}
      {e.common_causes !== null ? (
        <Block title={EXPLANATION_BLOCKS[3]}>
          <Text>{e.common_causes}</Text>
        </Block>
      ) : null}
      {fixes.length > 0 ? (
        <Block title={EXPLANATION_BLOCKS[4]}>
          <dl className="flex flex-col gap-2">
            {fixes.map(([label, text]) => (
              <div key={label}>
                <dt className="text-body-sm font-medium text-navy-700">{label}</dt>
                <dd className="text-body text-navy-950">{text}</dd>
              </div>
            ))}
          </dl>
        </Block>
      ) : null}
    </div>
  )
}
